import { useEffect, useState } from 'react';
import { api, setStoredUser, setToken, type CrmPrincipal } from '../lib/api';
import { Icon } from './icons';

export function LoginPage({ onAuthed }: { onAuthed: (u: CrmPrincipal) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bootstrapOpen, setBootstrapOpen] = useState(false);
  const [bootstrapAvailable, setBootstrapAvailable] = useState<boolean | null>(null);

  useEffect(() => {
    api.get<{ status: string; bootstrapAvailable: boolean }>('/api/crm/health')
      .then((r) => setBootstrapAvailable(r.bootstrapAvailable))
      .catch(() => setBootstrapAvailable(false));
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ token: string; user: CrmPrincipal }>('/api/crm/auth/login', { username, password });
      setToken(r.token);
      setStoredUser(r.user);
      onAuthed(r.user);
    } catch (e: any) {
      setError('Неверный логин или пароль');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-stone-950 bg-grid relative overflow-hidden">
      <div className="absolute inset-0 bg-noise opacity-30 pointer-events-none" />
      <div className="absolute -top-40 -left-40 w-[420px] h-[420px] rounded-full bg-amber-500/10 blur-3xl" />
      <div className="absolute -bottom-40 -right-40 w-[420px] h-[420px] rounded-full bg-amber-700/10 blur-3xl" />

      <div className="relative w-full max-w-md anim-up">
        <div className="flex flex-col items-center mb-8">
          <div className="relative w-16 h-16 mb-3">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-amber-300 to-amber-700 shadow-[0_0_32px_-4px_rgba(251,191,36,0.55)]" />
            <div className="absolute inset-[3px] rounded-xl bg-stone-950 flex items-center justify-center text-amber-300 font-display font-bold text-2xl">БК</div>
          </div>
          <h1 className="text-2xl font-display font-bold tracking-tight text-gradient-gold">BuraClub CRM</h1>
          <p className="text-sm text-stone-400 mt-1">Панель управления клубом</p>
        </div>

        <form onSubmit={submit} className="glass rounded-2xl p-6 space-y-4">
          <div>
            <label className="label">Логин</label>
            <input
              className="input mt-1.5"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
              minLength={3}
            />
          </div>
          <div>
            <label className="label">Пароль</label>
            <input
              className="input mt-1.5"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              minLength={8}
            />
          </div>
          {error && (
            <div className="rounded-lg border border-red-400/20 bg-red-500/10 text-red-300 px-3 py-2 text-sm">
              {error}
            </div>
          )}
          <button className="btn btn-gold w-full" disabled={busy}>
            {busy ? 'Входим…' : 'Войти'}
          </button>
        </form>

        {bootstrapAvailable && (
          <div className="mt-4 text-center">
            <button
              className="text-xs text-stone-400 hover:text-amber-300 transition-colors inline-flex items-center gap-1.5"
              onClick={() => setBootstrapOpen(true)}
            >
              <Icon.Spark size={12} />
              Первый вход: создать суперадмина
            </button>
          </div>
        )}

        {bootstrapOpen && (
          <BootstrapModal
            onClose={() => setBootstrapOpen(false)}
            onDone={(u) => {
              setStoredUser(u);
              onAuthed(u);
            }}
          />
        )}

        <p className="mt-8 text-center text-[11px] text-stone-600">
          © BuraClub • Доступ только для авторизованного персонала
        </p>
      </div>
    </div>
  );
}

function BootstrapModal({ onClose, onDone }: { onClose: () => void; onDone: (u: CrmPrincipal) => void }) {
  const [token, setTok] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await api.post<{ token: string; user: CrmPrincipal }>('/api/crm/auth/bootstrap', {
        token, username, password, displayName: displayName || username
      });
      setToken(r.token);
      onDone(r.user);
    } catch (e: any) {
      setErr(e?.error === 'BAD_TOKEN' ? 'Неверный bootstrap-токен' : e?.error === 'BOOTSTRAP_DONE' ? 'Суперадмин уже создан' : 'Ошибка');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 anim-fade" onClick={onClose}>
      <div className="glass-strong rounded-2xl p-6 w-full max-w-md anim-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Создать суперадмина</h2>
          <button onClick={onClose} className="p-1 rounded-md text-stone-400 hover:text-white hover:bg-white/5">
            <Icon.Close size={16} />
          </button>
        </div>
        <p className="text-xs text-stone-400 mb-4">Одноразовая процедура. Потребуется bootstrap-токен из переменной окружения сервера.</p>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="label">Bootstrap-токен</label>
            <input className="input mt-1.5" value={token} onChange={(e) => setTok(e.target.value)} required minLength={16} />
          </div>
          <div>
            <label className="label">Логин</label>
            <input className="input mt-1.5" value={username} onChange={(e) => setUsername(e.target.value)} required minLength={3} />
          </div>
          <div>
            <label className="label">Пароль (≥ 8)</label>
            <input className="input mt-1.5" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
          </div>
          <div>
            <label className="label">Имя для отображения</label>
            <input className="input mt-1.5" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={username || 'Администратор'} />
          </div>
          {err && <div className="rounded-lg border border-red-400/20 bg-red-500/10 text-red-300 px-3 py-2 text-sm">{err}</div>}
          <button className="btn btn-gold w-full" disabled={busy}>
            {busy ? 'Создаём…' : 'Создать суперадмина'}
          </button>
        </form>
      </div>
    </div>
  );
}
